export const PAGE = { widthMm: 210, heightMm: 297 } as const

export type SheetLayoutId = 'standard' | 'compact'

export interface SheetLayout {
  id: SheetLayoutId
  columns: number
  rows: number
  cardWidthMm: number
  cardHeightMm: number
  /** `marks` — chetdagi belgilar (keng chet), `grid` — kartalar orasidagi ingichka chiziq */
  cut: 'marks' | 'grid'
}

// Standard: ISO/IEC 7810 ID-1 karta. Compact: printer chetiga (~5 mm) sig'adigan eng katta 5×8.
export const LAYOUTS: Readonly<Record<SheetLayoutId, SheetLayout>> = {
  standard: { id: 'standard', columns: 2, rows: 5, cardWidthMm: 85, cardHeightMm: 54, cut: 'marks' },
  compact: { id: 'compact', columns: 5, rows: 8, cardWidthMm: 40, cardHeightMm: 35.5, cut: 'grid' },
}

export const DEFAULT_LAYOUT: SheetLayoutId = 'compact'

export const COMPACT_CARD = {
  paddingMm: 1.2,
  gapMm: 0.5,
  headerMm: 2.8,
  planMm: 2.8,
  qrMm: 21,
  codeHeightMm: 4.9,
  codeMaxFontMm: 3.2,
  /** Login va parolli kartada QR kichrayadi: ikki qator yorliqli qiymatga joy kerak */
  credentialQrMm: 17,
  credentialRowMm: 4.2,
  /** QR'siz kartada tartib raqami alohida qatorda */
  serialMm: 2.6,
} as const

/** Yorliq («Login», «Parol») ustuni va qiymatgacha bo'lgan oraliq */
export const CREDENTIAL_LABEL = { compactMm: 10.5, standardMm: 11, gapMm: 0.8 } as const

/** Serverning `VoucherPrintSelection.MAX_CARDS` i bilan bir xil */
export const MAX_CARDS_PER_PRINT = 1000

export function cardsPerPage(layout: SheetLayout): number {
  return layout.columns * layout.rows
}

export function sheetMargins(layout: SheetLayout): { x: number; y: number } {
  return {
    x: (PAGE.widthMm - layout.columns * layout.cardWidthMm) / 2,
    y: (PAGE.heightMm - layout.rows * layout.cardHeightMm) / 2,
  }
}

export function paginate<T>(cards: readonly T[], layout: SheetLayout): T[][] {
  const perPage = cardsPerPage(layout)
  const pages: T[][] = []
  for (let i = 0; i < cards.length; i += perPage) pages.push(cards.slice(i, i + perPage))
  return pages
}

/** Kesish chiziqlari — varaq chetidan o'lchangan, mm */
export function cutLines(layout: SheetLayout): { xs: number[]; ys: number[] } {
  const { x, y } = sheetMargins(layout)
  const xs = Array.from({ length: layout.columns + 1 }, (_, i) => x + i * layout.cardWidthMm)
  const ys = Array.from({ length: layout.rows + 1 }, (_, i) => y + i * layout.cardHeightMm)
  return { xs, ys }
}

export function sizeLabel(layout: SheetLayout): string {
  return `${layout.cardWidthMm}×${layout.cardHeightMm}`
}

const LAYOUT_STORAGE_KEY = 'xsib.voucher.printLayout'

export function loadLayoutChoice(): SheetLayoutId {
  try {
    const saved = localStorage.getItem(LAYOUT_STORAGE_KEY)
    return saved === 'standard' || saved === 'compact' ? saved : DEFAULT_LAYOUT
  } catch {
    return DEFAULT_LAYOUT
  }
}

export function saveLayoutChoice(id: SheetLayoutId): void {
  try {
    localStorage.setItem(LAYOUT_STORAGE_KEY, id)
  } catch {
    // Saqlanmasa keyingi safar standart tanlov ochiladi.
  }
}

/** `#12-0037` — kartani kodsiz topish uchun; nollar partiya hajmiga qarab */
export function serialLabel(batchNo: number, serialNo: number, quantity: number): string {
  const width = Math.max(4, String(quantity).length)
  return `#${batchNo}-${String(serialNo).padStart(width, '0')}`
}

/** `#12-37`, `12-0037`, `12/37` — operator qanday yozsa ham */
export function parseCardRef(raw: string): { batchNo: number; serialNo: number } | null {
  const match = /^#?\s*(\d{1,12})\s*[-/ ]\s*(\d{1,9})$/.exec(raw.trim())
  if (!match) return null
  const batchNo = Number(match[1])
  const serialNo = Number(match[2])
  return batchNo > 0 && serialNo > 0 ? { batchNo, serialNo } : null
}

/**
 * QR ichidagi matn. Portal manzili bo'lsa — `<portal>/v#t.<token>`, token
 * hali yo'q eski kartada `<portal>/v#<kod>`: ikkalasi ham fragmentda, ya'ni
 * server log va Referer'ga tushmaydi. Portal bo'lmasa — kodning o'zi.
 */
export function qrContent(portalUrl: string | null, card: { code: string; qrToken: string | null }): string {
  if (!portalUrl) return card.code
  const base = `${portalUrl.replace(/\/+$/, '')}/v#`
  return card.qrToken ? `${base}t.${encodeURIComponent(card.qrToken)}` : `${base}${encodeURIComponent(card.code)}`
}

// Monoshriftda belgi ~0,6 em, oraliq 0,05 em — 0,67 zaxira bilan.
const MONO_ADVANCE_EM = 0.67
export const CODE_LINE_HEIGHT = 1.1

/**
 * Kod 44 belgigacha bo'lishi mumkin (prefiks 12 + 32). Eng katta shrift
 * beradigan qatorlar soni tanlanadi; teng bo'lsa — kamrog'i.
 */
export function fitCode(
  code: string,
  box: { widthMm: number; heightMm: number; maxFontMm: number; maxLines: number },
): { lines: string[]; fontMm: number } {
  const length = Math.max(code.length, 1)
  let best = { lineCount: 1, fontMm: 0 }
  for (let lineCount = 1; lineCount <= box.maxLines; lineCount++) {
    const perLine = Math.ceil(length / lineCount)
    const fontMm = Math.min(
      box.maxFontMm,
      box.widthMm / (perLine * MONO_ADVANCE_EM),
      box.heightMm / (lineCount * CODE_LINE_HEIGHT),
    )
    if (fontMm > best.fontMm) best = { lineCount, fontMm }
  }
  const perLine = Math.ceil(length / best.lineCount)
  const lines = Array.from({ length: best.lineCount }, (_, i) => code.slice(i * perLine, (i + 1) * perLine))
  return { lines, fontMm: Math.floor(best.fontMm * 100) / 100 }
}

export const STANDARD_CODE_BOX = { widthMm: 78, heightMm: 6, maxFontMm: 4.2, maxLines: 1 } as const

const COMPACT_INNER_MM = LAYOUTS.compact.cardWidthMm - 2 * COMPACT_CARD.paddingMm

export const COMPACT_CODE_BOX = {
  widthMm: COMPACT_INNER_MM,
  heightMm: COMPACT_CARD.codeHeightMm,
  maxFontMm: COMPACT_CARD.codeMaxFontMm,
  maxLines: 2,
} as const

/** QR'siz ixcham karta — kod bo'shagan joyni egallaydi */
export const COMPACT_LOOSE_CODE_BOX = { widthMm: COMPACT_INNER_MM, heightMm: 10, maxFontMm: 4.6, maxLines: 2 } as const

const COMPACT_CREDENTIAL_WIDTH_MM = COMPACT_INNER_MM - CREDENTIAL_LABEL.compactMm - CREDENTIAL_LABEL.gapMm

export const COMPACT_CREDENTIAL_BOX = {
  widthMm: COMPACT_CREDENTIAL_WIDTH_MM,
  heightMm: COMPACT_CARD.credentialRowMm,
  maxFontMm: 3.4,
  maxLines: 1,
} as const

export const COMPACT_LOOSE_CREDENTIAL_BOX = {
  widthMm: COMPACT_CREDENTIAL_WIDTH_MM,
  heightMm: 7,
  maxFontMm: 4.6,
  maxLines: 1,
} as const

/** Standart kartada login va parol bitta qatorda yonma-yon, kod qatori o'rnida */
export const STANDARD_CREDENTIAL_GAP_MM = 3

export const STANDARD_CREDENTIAL_BOX = {
  widthMm:
    (STANDARD_CODE_BOX.widthMm - STANDARD_CREDENTIAL_GAP_MM) / 2 - CREDENTIAL_LABEL.standardMm - CREDENTIAL_LABEL.gapMm,
  heightMm: STANDARD_CODE_BOX.heightMm,
  maxFontMm: STANDARD_CODE_BOX.maxFontMm,
  maxLines: 1,
} as const

/** Oraliqni partiya chegarasiga va bitta varaq limitiga moslaydi */
export function clampRange(from: number, to: number, quantity: number): { from: number; to: number } {
  const start = Math.min(Math.max(1, Math.floor(from)), quantity)
  const end = Math.min(Math.max(start, Math.floor(to)), quantity, start + MAX_CARDS_PER_PRINT - 1)
  return { from: start, to: end }
}
